import { Grid } from "@mui/material";
import { type FC, useCallback, useEffect, useState } from "react";
import { PaginationWrapper } from "../../generic/components/pagination-wrapper";
import { SearchField } from "../../search/search-field";
import { AssetCard } from "../asset-card";
import { contentTypes } from "../content-types";
import type { Asset } from "../server/digital-asset-service";

import { AssetPickerAddNew } from "./asset-add-new";
import { ContentLibraryFilter } from "./content-library-filter";

export const AssetPicker: FC<{
  setPreviewAsset: (asset: Asset | null) => void;
  selectedAsset: Asset | null;
  setSelectedAsset: (asset: Asset | null) => void;
  assets: Asset[];
  onUseAsLink?: (url: string, title: string) => void;
  children?: React.ReactNode;
}> = ({ setPreviewAsset, selectedAsset, setSelectedAsset, assets, onUseAsLink, children }) => {
  const [searchText, setSearchText] = useState<string>("");
  const [selectedContentTypes, setSelectedContentTypes] = useState<string[]>(contentTypes);
  const [appliedContentTypes, setAppliedContentTypes] = useState<string[]>([]);
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [appliedTags, setAppliedTags] = useState<string[]>([]);
  const [availableTags, setAvailableTags] = useState<string[]>([]);
  const [filteredAssets, setFilteredAssets] = useState(assets);

  const handleApplyFilter = useCallback(() => {
    setAppliedContentTypes(selectedContentTypes);
    setAppliedTags(selectedTags);
  }, [selectedContentTypes, selectedTags]);

  useEffect(() => {
    // Extract all unique tags from assets
    const allTags = Array.from(
      new Set(
        assets
          .filter((asset) => asset.tags && asset.tags.length > 0)
          .flatMap((asset) => asset.tags!),
      ),
    ).sort();

    // eslint-disable-next-line react-hooks/set-state-in-effect
    setAvailableTags(allTags);

    if (!searchText.trim()) {
      setFilteredAssets(
        assets.filter((a) => {
          const contentTypeMatch =
            appliedContentTypes.length > 0 ? appliedContentTypes.includes(a.content_type) : true;
          const tagMatch =
            appliedTags.length > 0
              ? a.tags && a.tags.some((tag) => appliedTags.includes(tag))
              : true;
          return contentTypeMatch && tagMatch;
        }),
      );
      return;
    }

    const f = searchText.trim().toLowerCase();
    setFilteredAssets(
      assets.filter((a) => {
        const textMatch =
          a.title.toLowerCase().includes(f) || a.content_type.toLowerCase().includes(f);
        const contentTypeMatch =
          appliedContentTypes.length > 0 ? appliedContentTypes.includes(a.content_type) : true;
        const tagMatch =
          appliedTags.length > 0 ? a.tags && a.tags.some((tag) => appliedTags.includes(tag)) : true;
        return textMatch && contentTypeMatch && tagMatch;
      }),
    );
  }, [appliedContentTypes, appliedTags, assets, searchText]);

  return (
    <Grid container spacing={4}>
      <Grid size={{ xs: 9 }} sx={{ pt: 1 }}>
        <SearchField
          defaultValue={searchText ?? ""}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            setSearchText(e.target.value);
          }}
        />
      </Grid>
      <Grid size={{ xs: 1 }} sx={{ pt: 1 }}>
        <ContentLibraryFilter
          handleApplyFilter={handleApplyFilter}
          selectedContentTypes={selectedContentTypes}
          setSelectedContentTypes={setSelectedContentTypes}
          contentTypes={contentTypes}
          selectedTags={selectedTags}
          setSelectedTags={setSelectedTags}
          availableTags={availableTags}
        />
      </Grid>
      {!onUseAsLink && (
        <Grid size={{ xs: 2 }} sx={{ pt: 1 }} justifyContent={"flex-end"}>
          {children ? children : <AssetPickerAddNew setSelectedAsset={setSelectedAsset} />}
        </Grid>
      )}
      <PaginationWrapper
        data={filteredAssets}
        itemsPerPage={25}
        renderPage={(data) => {
          return data.map((asset) => (
            <Grid key={asset.id} size={4}>
              <AssetCard
                asset={asset}
                selected={asset.id === selectedAsset?.id}
                data-testid="asset"
                onClick={() => {
                  asset.id === selectedAsset?.id && !onUseAsLink
                    ? setPreviewAsset(asset)
                    : setSelectedAsset(asset);
                }}
                disableZoomIcon={!!onUseAsLink}
                folderName={null}
              />
            </Grid>
          ));
        }}
      />
    </Grid>
  );
};
